import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../store/slices/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import logo1 from "../../assets/Images/logo1.png";
import logo2 from "../../assets/Images/logo2.png";

const schema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup.string().required("Password is required"),
});

const SignInForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginLoading = useSelector((state) => state.auth.loading);
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      setServerError("");
      const resultAction = await dispatch(
        login({ email: data.email, password: data.password })
      );

      if (login.fulfilled.match(resultAction)) {
        const userToken = resultAction.payload.token;
        localStorage.setItem("token", userToken);
        navigate("/");
      } else {
        setServerError(
          resultAction.payload?.message || "Invalid email or password"
        );
      }
    } catch (error) {
      setServerError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <div className=" flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-md w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src={logo1}
              onClick={() => navigate("/")}
              alt="Logo for Light Mode"
              className="block dark:hidden h-10 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            />
            <img
              src={logo2}
              onClick={() => navigate("/")}
              alt="Logo for Dark Mode"
              className="hidden dark:block h-10 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            />
          </div>
        
          <p className="text-gray-600 dark:text-gray-300">
            Access your favorite tools and continue where you left off
          </p>
        </div>

        {(serverError || Object.keys(errors).length > 0) && (
          <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 rounded-lg text-sm">
            {serverError ||
              Object.values(errors)[0]?.message ||
              "Please fix the errors below"}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Email Address
            </label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="email"
                  id="email"
                  placeholder="your@email.com"
                  className={`w-full px-4 py-2 border ${
                    errors.email
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500"
                  } rounded-lg focus:outline-none focus:ring-1 transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                  disabled={loginLoading}
                />
              )}
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Password
            </label>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <div className="relative">
                  <input
                    {...field}
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="••••••••"
                    className={`w-full px-4 py-2 border ${
                      errors.password
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500"
                    } rounded-lg focus:outline-none focus:ring-1 transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                    disabled={loginLoading}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loginLoading}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              )}
            />
            <div className="flex justify-end mt-1">
              <button
                type="button"
                className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 focus:outline-none"
                onClick={() => navigate("/forgot-password")}
                disabled={loginLoading}
              >
                Forgot password?
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="w-full mt-2 bg-[#74D19C] hover:bg-[#74D19C] text-white font-medium py-2.5 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={loginLoading}
          >
            {loginLoading ? (
              <span className="flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faCircleNotch}
                  className="animate-spin mr-2"
                />
                Signing in...
              </span>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                Or continue with
              </span>
            </div>
          </div>

          
        </div>

        <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Don't have an account?{" "}
          <button
            className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium focus:outline-none focus:underline"
            onClick={() => navigate("/registration")}
            disabled={loginLoading}
          >
            Register
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignInForm;